import React, { useState, useEffect } from "react";
import { Constants } from "@eagerdog/constants";
import { IEventSummary, IShow, IShowElement, ShowDto } from "@eagerdog/interfaces";
import { AxiosError } from "axios";

import { apiService } from "src/services";
import { toast } from "src/components/Toast/ToastManager";
import Input from "src/components/Input/Input";
import Button from "src/components/Button/Button";

import styles from "./ScentworkElementsTab.module.scss";

interface IProps {
  event: IEventSummary;
  show: IShow;
  onUpdate(): void;
}

interface IExtendedShowElement extends IShowElement {
  num_hides?: number;
  time_limit?: string;
}

const ScentworkElementsTab: React.FC<IProps> = (props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [showElements, setShowElements] = useState<IExtendedShowElement[]>([]);
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  useEffect(() => {
    // Initialize with current show elements
    setShowElements([...props.show.show_elements]);
    setHasChanges(false); // Reset changes flag when show data updates
  }, [props.show.show_elements]);

  const updateElement = (index: number, field: 'num_hides' | 'time_limit', value: string | number) => {
    const updatedElements = [...showElements];
    if (field === 'num_hides') {
      updatedElements[index] = {
        ...updatedElements[index],
        num_hides: typeof value === 'string' ? parseInt(value) || 0 : value
      };
    } else {
      updatedElements[index] = {
        ...updatedElements[index],
        time_limit: value.toString()
      };
    }
    setShowElements(updatedElements);
    setHasChanges(true);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Create a clean payload without version-related fields to avoid conflicts
      const updatedShow: ShowDto = {
        ...props.show,
        show_elements: showElements
      };

      // Remove version-related fields that could cause conflicts
      delete (updatedShow as any).__v;
      delete (updatedShow as any).updated_at;
      delete (updatedShow as any).created_at;

      await apiService.updateShow(props.event._id, props.show._id, updatedShow);
      
      toast.show({
        title: "Scentwork Elements",
        content: "Element settings have been saved successfully",
        duration: 5000,
        type: "success"
      });

      setHasChanges(false);
      props.onUpdate();
    } catch (error) {
      toast.show({
        title: "Scentwork Elements",
        content: "Failed to save element settings",
        errorDetails: error as AxiosError,
        duration: 10000,
        type: "fail"
      });
    } finally {
      setLoading(false);
    }
  };

  const getScentworkElements = () => {
    return showElements.filter(element => 
      [
        Constants.dog_class_element.scentwork_container,
        Constants.dog_class_element.scentwork_interior,
        Constants.dog_class_element.scentwork_exterior,
        Constants.dog_class_element.scentwork_buried,
        Constants.dog_class_element.scentwork_handler_disc,
        Constants.dog_class_element.scentwork_detective
      ].includes(element.show_element)
    );
  };

  const scentworkElements = getScentworkElements();

  return (
    <div className={styles.ScentworkElementsTab}>
      <div className={styles.details}>
        <p>
          Configure the number of hides and time limits for each scentwork element. 
          These values will be used on scoresheets and for results entry.
        </p>
      </div>

      {scentworkElements.map((element, index) => {
        const originalIndex = showElements.findIndex(se => 
          se.show_element === element.show_element && 
          se.judge_name === element.judge_name
        );

        return (
          <div key={`${element.show_element}-${index}`} className={styles.ElementConfig}>
            <div className={styles.title}>
              <div className={styles.titleInner}>
                {element.show_element}
                {element.judge_name && (
                  <span className={styles.judgeName}>Judge: {element.judge_name}</span>
                )}
              </div>
            </div>
            
            <div className={styles.configRow}>
              <div className={styles.inputGroup}>
                <Input
                  label="Number of Hides"
                  id={`num_hides_${originalIndex}`}
                  type="number"
                  min="0"
                  max="20"
                  defaultValue={element.num_hides?.toString() || "0"}
                  onChange={(e) => {
                    e.stopPropagation();
                    updateElement(originalIndex, 'num_hides', e.target.value);
                  }}
                  placeholder="0"
                />
              </div>
              
              <div className={styles.inputGroup}>
                <Input
                  label="Time Limit"
                  id={`time_limit_${originalIndex}`}
                  type="text"
                  defaultValue={element.time_limit || ""}
                  onChange={(e) => {
                    e.stopPropagation();
                    updateElement(originalIndex, 'time_limit', e.target.value);
                  }}
                  placeholder="e.g., 3:00"
                />
              </div>
            </div>
          </div>
        );
      })}

      {scentworkElements.length === 0 && (
        <div className={styles.noElements}>
          <p>No scentwork elements found for this show.</p>
        </div>
      )}

      <div className={styles.actions}>
        <Button 
          onClick={handleSave} 
          disabled={!hasChanges || loading}
          isLoading={loading}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default ScentworkElementsTab; 